/**
 * Production Utilities
 * Handles production-specific configurations and optimizations
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Conditional console logging - only logs in development
 * @param {string} level - log level (log, error, warn, info)
 * @param {...any} args - arguments to log
 */
export const conditionalLog = (level, ...args) => {
  if (isDevelopment) {
    console[level]?.(...args);
  }
};

/**
 * Safe console methods that only work in development
 */
export const safeConsole = {
  log: (...args) => conditionalLog('log', ...args),
  error: (...args) => conditionalLog('error', ...args),
  warn: (...args) => conditionalLog('warn', ...args),
  info: (...args) => conditionalLog('info', ...args),
  debug: (...args) => conditionalLog('debug', ...args),
  trace: (...args) => conditionalLog('trace', ...args),
  table: (...args) => isDevelopment && console.table?.(...args),
  group: (...args) => isDevelopment && console.group?.(...args),
  groupEnd: () => isDevelopment && console.groupEnd?.(),
  time: (label) => isDevelopment && console.time?.(label),
  timeEnd: (label) => isDevelopment && console.timeEnd?.(label)
};

/**
 * Production-safe error reporting
 * In production, logs to external service; in development, logs to console
 */
export const reportError = (error, context = {}) => {
  if (isDevelopment) {
    console.error('🚨 Error:', error);
    console.error('📍 Context:', context);
  } else {
    // In production, send to external logging service
    // External logging service integration point
    // Configure via environment variables: REACT_APP_ERROR_LOGGING_SERVICE
  }
};

/**
 * Performance monitoring helper
 */
export const performanceMark = (name) => {
  if (isDevelopment && window.performance?.mark) {
    window.performance.mark(name);
  }
};

/**
 * Clean object for production logging (removes sensitive data)
 */
export const sanitizeForLogging = (obj) => {
  const sensitive = ['password', 'token', 'key', 'secret', 'auth'];
  
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  const cleaned = { ...obj };
  
  Object.keys(cleaned).forEach(key => {
    if (sensitive.some(term => key.toLowerCase().includes(term))) {
      cleaned[key] = '[REDACTED]';
    } else if (typeof cleaned[key] === 'object') {
      cleaned[key] = sanitizeForLogging(cleaned[key]);
    }
  });

  return cleaned;
};

/**
 * Environment-specific configuration
 */
export const config = {
  isDevelopment,
  isProduction,
  enableDebugMode: isDevelopment,
  enablePerformanceMonitoring: isDevelopment,
  enableDetailedErrorReporting: isDevelopment,
  
  // API Configuration
  enableRequestLogging: isDevelopment,
  enableResponseLogging: isDevelopment,
  
  // WebSocket Configuration
  enableSocketLogging: isDevelopment,
  socketReconnectionAttempts: isProduction ? 5 : 10,
  socketTimeout: isProduction ? 15000 : 20000
};

export default {
  conditionalLog,
  safeConsole,
  reportError,
  performanceMark,
  sanitizeForLogging,
  config,
  isDevelopment,
  isProduction
};